/**
 * Remittance hooks
 *
 * - useRemittanceAccess: who may open the remittance screens. Reserved to OGF
 *   structures holding ADMIN, CASH_TRACKING, GLOBAL_CASH_TRACKING or
 *   AGENCY_HEAD. ADMIN and GLOBAL_CASH_TRACKING see every agency of the
 *   structure (multi-agency filter, agency picked when recording), the two
 *   others only their own agency. Export stays ADMIN only. The backend
 *   applies the same rules; this only drives the UI.
 * - useRemittanceReminders: late / critical remittances, shared by the header
 *   bell and the dashboard banner (same query key, one request).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../store/AuthContext';
import remittancesService from '../services/remittances';

export const REMITTANCE_PROFILES = ['ADMIN', 'CASH_TRACKING', 'GLOBAL_CASH_TRACKING', 'AGENCY_HEAD'];
export const REMITTANCE_STRUCTURE_TYPES = ['OGF'];

export const REMITTANCE_REMINDERS_QUERY_KEY = ['remittance-reminders'];

// Reminders only move when a payment is confirmed or a remittance recorded:
// a 5-minute refresh is plenty, and recording a remittance invalidates the key.
const REMINDERS_REFRESH_MS = 5 * 60 * 1000;

export interface RemittanceAccess {
  canAccess: boolean;
  isAdmin: boolean;
  /** ADMIN or GLOBAL_CASH_TRACKING: every agency of the structure. */
  isStructureWide: boolean;
}

export function useRemittanceAccess(): RemittanceAccess {
  const { user } = useAuth();

  return useMemo(() => {
    const profiles = user?.profiles ?? [];
    const isOgf = Boolean(
      user?.structure_type && REMITTANCE_STRUCTURE_TYPES.includes(user.structure_type),
    );
    const canAccess = isOgf && REMITTANCE_PROFILES.some((code) => profiles.includes(code));
    const isAdmin = canAccess && profiles.includes('ADMIN');
    const isStructureWide = isAdmin || (canAccess && profiles.includes('GLOBAL_CASH_TRACKING'));
    return { canAccess, isAdmin, isStructureWide };
  }, [user?.profiles, user?.structure_type]);
}

export function useRemittanceReminders() {
  const { canAccess } = useRemittanceAccess();

  return useQuery({
    queryKey: REMITTANCE_REMINDERS_QUERY_KEY,
    queryFn: () => remittancesService.getReminders(),
    enabled: canAccess,
    staleTime: 60 * 1000,
    refetchInterval: REMINDERS_REFRESH_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: false,
  });
}
